import { Modal } from '../ui/Modal';
import { VehicleGraphic } from './VehicleGraphic';
import { VehicleCameraViewer } from './VehicleCameraViewer';
import { formatSpeed, formatKm } from '../../utils/formatters';
import { VehicleMaintenanceBars } from './VehicleMaintenanceBars';
import { User, MapPin, Cpu, Shield, Gauge, Wrench } from 'lucide-react';
import type { Vehicle } from '../../types/vehicle';
import { useAuth } from '../../context/AuthContext';

interface VehicleDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  vehicle: Vehicle | null;
}

export function VehicleDetailModal({ isOpen, onClose, vehicle }: VehicleDetailModalProps) {
  // FE-14: el Operador no tiene permiso para ver video en vivo (PRODUCT.md
  // §4.8).
  const { user } = useAuth();
  const canViewVideo = user?.role !== 'operator';

  if (!vehicle) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Vehículo: ${vehicle.plate}`}
      size="md"
    >
      <div className="vehicle-detail-modal" style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 600 }}>
          <Shield size={15} /> Información General
        </div>

        <VehicleGraphic status={vehicle.status} showCaption />

            {/* Header Title */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '0.5rem' }}>
              <div>
                <h2 style={{ fontSize: '22px', fontWeight: 800, color: 'var(--text-primary)', margin: 0 }}>{vehicle.plate}</h2>
                <p style={{ fontSize: '14px', color: 'var(--text-muted)', margin: '2px 0 0 0' }}>{vehicle.brand} {vehicle.model} ({vehicle.year})</p>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block' }}>Unidad</span>
                <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--accent)' }}>{vehicle.subUnitName}</span>
              </div>
            </div>

            {/* Specs Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.875rem' }}>
              <div style={{ background: 'var(--bg-raised)', padding: '0.875rem 1rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <User size={18} style={{ color: 'var(--accent)', flexShrink: 0 }} />
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>Conductor Asignado</span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{vehicle.driverName || 'Sin asignar'}</span>
                </div>
              </div>

              <div style={{ background: 'var(--bg-raised)', padding: '0.875rem 1rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Cpu size={18} style={{ color: '#8B5CF6', flexShrink: 0 }} />
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>ID Dispositivo GPS</span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>{vehicle.deviceId || 'Sin equipo instalado'}</span>
                </div>
              </div>

              <div style={{ background: 'var(--bg-raised)', padding: '0.875rem 1rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <MapPin size={18} style={{ color: '#10B981', flexShrink: 0 }} />
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>Ubicación GPS</span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {vehicle.position.lat.toFixed(4)}, {vehicle.position.lng.toFixed(4)}
                  </span>
                </div>
              </div>

              <div style={{ background: 'var(--bg-raised)', padding: '0.875rem 1rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Gauge size={18} style={{ color: '#F59E0B', flexShrink: 0 }} />
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>Velocidad Actual</span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {vehicle.position.speed !== undefined ? formatSpeed(vehicle.position.speed) : '0 km/h'}
                  </span>
                </div>
              </div>

              {/* FE-19 — odómetro del EQUIPO (BE-50). La etiqueta dice «del equipo»
                  a propósito: al cambiar el dispositivo el número reinicia y puede
                  bajar, así que no es el kilometraje del vehículo. La fecha de
                  medición no se muestra (decisión del dueño, 2026-09-11). */}
              <div style={{ background: 'var(--bg-raised)', padding: '0.875rem 1rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                <Gauge size={18} style={{ color: '#3B82F6', flexShrink: 0 }} />
                <div>
                  <span style={{ fontSize: '11px', color: 'var(--text-muted)', display: 'block' }}>Odómetro del equipo</span>
                  <span style={{ fontSize: '13px', fontWeight: 700, color: 'var(--text-primary)' }}>
                    {vehicle.odometer ? formatKm(vehicle.odometer.km) : '—'}
                  </span>
                </div>
              </div>
            </div>

            {/* FE-23 — vencimientos. Solo lectura por ahora: editar las fechas y
                registrar el cambio de aceite necesita los endpoints de BE-58
                (`PATCH .../documents`, `POST .../oil-change`). La pestaña que
                existía antes tenía un "Guardar" que solo hacía `alert(...)` y no
                guardaba nada; volver a ponerla sin persistencia sería el mismo
                engaño. */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-secondary)', fontSize: '13px', fontWeight: 600 }}>
                <Wrench size={15} /> Vencimientos y mantenimiento
              </div>
              <div style={{ background: 'var(--bg-raised)', padding: '0.875rem 1rem', borderRadius: 'var(--radius-lg)', border: '1px solid var(--border)' }}>
                <VehicleMaintenanceBars vehicle={vehicle} mode="detail" />
              </div>
            </div>

            {/* Video en vivo (FE-14) — el Operador no ve esta sección en absoluto */}
            {canViewVideo && (
              <VehicleCameraViewer vehicleId={vehicle.id} />
            )}

      </div>
    </Modal>
  );
}
